/**
 * O selo de estado do sistema — em formato de CHIP (pill de 999px).
 *
 * Existe como componente único porque selo escrito à mão em cada tela é como
 * o sistema acaba com quatro formatos de "ativo": um com raio 6, um com raio
 * 999, um verde e um azul. O catálogo de componentes
 * (`design/prototipos/Lumma Fit Componentes.dc.html`, seção "Badges e tags")
 * desenha o estado em pill com ponto colorido à esquerda, e é esse o formato
 * que vale.
 *
 * A COR segue a spec 01, e a regra é dura: verde/âmbar/vermelho são reservados
 * a ESTADO, nunca a decoração. O desfecho normal — "lançada", "recebida",
 * "paga" — é NEUTRO: cor em quase toda linha não classifica nada, só cansa.
 * Guarde âmbar para o que espera alguém hoje e vermelho para o que travou.
 */
import type { ReactNode } from 'react'

/**
 * `neutro` é o desfecho normal e o inativo; `info` é "andou, mas não produziu
 * efeito ainda"; `ok` é o que exigia confirmação e passou; `atencao` pede
 * decisão; `erro` travou; `roxo` é o fluxo invertido — devolução e
 * inutilização, que não são falha nem sucesso, são o caminho de volta.
 * `riscado` é documento fora do ciclo (cancelado) — ele tem forma própria, e
 * não cor própria, porque cancelado não é urgência.
 */
export type TomSelo = 'neutro' | 'info' | 'ok' | 'atencao' | 'erro' | 'roxo' | 'riscado'

/*
  Cada tom é o TINT como fundo e o tom ESCURO como texto — nunca o sólido.
  O sólido existe para ponto, barra e ícone; sobre o tint ele não contrasta.

  Isto já esteve errado e o erro era invisível na tela do desenvolvedor: o
  sólido `#16A34A` sobre o tint verde dá 3,00:1, e o sólido `#DC2626` sobre o
  tint vermelho dá 3,95:1 — contra os 4,5:1 que a WCAG pede em texto de 12px.
  Com o tom escuro da spec 01 sobem para 4,57:1 e 5,30:1. Um selo de "atraso"
  que some sob a luz da vitrine é um selo que não faz o trabalho dele.

  O neutro é o único que foge do valor da spec: `#6B7280` sobre `#F3F4F6` dá
  4,39:1 e reprova por pouco, então usa `texto` (9,37:1). Continua discreto —
  é cinza sobre cinza, e o que separa o desfecho normal dos outros é a
  AUSÊNCIA de cor, não o quão claro é o cinza.
*/
const TINTA: Record<TomSelo, string> = {
  neutro: 'bg-divisoria text-texto',
  info: 'bg-marca-suave text-marca-forte',
  ok: 'bg-ok-suave text-ok-texto',
  atencao: 'bg-atencao-suave text-atencao-texto',
  erro: 'bg-erro-suave text-erro-texto',
  roxo: 'bg-roxo-suave text-roxo-texto',
  riscado: 'bg-divisoria text-texto line-through',
}

/**
 * O selo é SÓ TEXTO dentro do chip.
 *
 * O catálogo desenha uma variante com pontinho colorido à esquerda, e ela saiu:
 * o ponto repete em forma a informação que a cor do chip já dá, e numa tabela
 * com uma linha por nota ele vira sujeira — seis pontos coloridos empilhados
 * competindo com o número que a pessoa foi ler. Onde o chip precisa de ícone,
 * o ícone entra como filho, com significado próprio.
 */
export function Selo({ tom = 'neutro', children }: { tom?: TomSelo; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap ${TINTA[tom]}`}
    >
      {children}
    </span>
  )
}
